import { useState, useEffect, useRef, useCallback } from "react";
import {
  ThumbsUp,
  ThumbsDown,
  Bookmark,
  ImageIcon,
  Copy,
  Check,
  FileText,
  Globe,
  Clock,
  Gavel,
  Users,
  Megaphone,
  Award,
  DollarSign,
  GraduationCap,
  FileSearch,
  Building2,
  Globe2,
  Crosshair,
  Newspaper,
  Shield,
  Radar,
  X,
  Search as SearchIcon,
} from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { toggleThumb } from "../../api/feed";
import { useRightSidebar } from "../contexts/RightSidebarContext";
import { useDark, getTheme } from "./useNavTheme";
import ShareArticleButton from "./ShareArticleButton";
import { motion, AnimatePresence } from "framer-motion";

/* ─── Domain-semantic badge colors — content classification, not UI chrome ─── */
const TYPE_BADGES = {
  court_cases: { icon: Gavel, color: "#f59e0b", label: "Court Case" },
  neighbors: { icon: Users, color: "#8b5cf6", label: "Neighbors" },
  press_release: { icon: Megaphone, color: "#10b981", label: "Press Release" },
  gov_award: { icon: Award, color: "#f97316", label: "Gov Award" },
  financial_crawl: { icon: DollarSign, color: "#06b6d4", label: "Financial" },
  edu_crawl: { icon: GraduationCap, color: "#ec4899", label: "Education" },
  investigative_doc: {
    icon: FileSearch,
    color: "#ef4444",
    label: "Investigative",
  },
  company_subsidiary: {
    icon: Building2,
    color: "#6366f1",
    label: "Subsidiary",
  },
  domain_crawl: { icon: Globe2, color: "#14b8a6", label: "Domain Crawl" },
  hunt: { icon: Radar, color: "#0ea5e9", label: "Hunt" },
  apt_news: { icon: Newspaper, color: "#e11d48", label: "APT News" },
  web_citation: { icon: SearchIcon, color: "#c5548ee3", label: "User Hunted" },
};

const TARGET_COLOR = "#22d3ee";
const APT_COLOR = "#f472b6";

export const TypeBadge = ({ type }) => {
  const badge = TYPE_BADGES[type];
  if (!badge) return null;
  const Icon = badge.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.625rem] font-normal whitespace-nowrap bg-innerBg"
      style={{ color: badge.color, border: `1px solid ${badge.color}40` }}
    >
      <Icon size={10} style={{ flexShrink: 0 }} />
      {badge.label}
    </span>
  );
};

export const TargetBadge = ({ target }) => {
  if (!target) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.625rem] font-normal whitespace-nowrap bg-innerBg max-w-[160px] overflow-hidden"
      style={{ color: TARGET_COLOR, border: `1px solid ${TARGET_COLOR}40` }}
    >
      <Crosshair size={10} className="shrink-0" />
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
        {target}
      </span>
    </span>
  );
};

export const AptBadge = ({ aptName }) => {
  if (!aptName) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[0.625rem] font-normal whitespace-nowrap bg-innerBg max-w-[160px] overflow-hidden"
      style={{ color: APT_COLOR, border: `1px solid ${APT_COLOR}40` }}
    >
      <Shield size={10} className="shrink-0" />
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">
        {aptName}
      </span>
    </span>
  );
};

/* ─── Action button — filled icon = on, outline icon = off ─── */
const SidebarActionBtn = ({ active, label, onClick, children }) => (
  <button
    type="button"
    aria-pressed={!!active}
    aria-label={label}
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    className={[
      "inline-flex items-center justify-center rounded-full h-10 w-10 border cursor-pointer",
      "outline-none ring-inputFocus/60 focus-visible:ring-2",
      "transition-all duration-150 active:scale-90",
      active
        ? "bg-velBlue/10 border-velBlue"
        : "bg-innerBg border-inputBorder hover:border-surfaceBorder",
    ].join(" ")}
  >
    {children}
  </button>
);

/* Lucide icons are stroke-based — color is all we need */
const iconStyle = (active) => ({
  color: active ? "var(--c-velBlue)" : "var(--c-mutedText)",
  transition: "color 150ms",
});

/* ═══════════════════════════════════════════
   ArticleDetailPanel
   `t` prop accepted for call-site compatibility; styling uses VN tokens internally.
   ═══════════════════════════════════════════ */
const ArticleDetailPanel = ({ article, cardId, t, onInteract, onClose }) => {
  const [viewMode, setViewMode] = useState("screenshot");
  const [localLike, setLocalLike] = useState(article?.like || false);
  const [localDislike, setLocalDislike] = useState(article?.dislike || false);
  const [localCatalog, setLocalCatalog] = useState(article?.catalog || false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [copied, setCopied] = useState(false);
  const { setIsRightOpen } = useRightSidebar();
  const contentRef = useRef(null);

  useEffect(() => {
    if (!article) return;
    setImgLoaded(false);
    setImgError(false);
    setCopied(false);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [article?.document_id]);

  useEffect(() => {
    setLocalLike(article?.like || false);
  }, [article?.like]);
  useEffect(() => {
    setLocalDislike(article?.dislike || false);
  }, [article?.dislike]);
  useEffect(() => {
    setLocalCatalog(article?.catalog || false);
  }, [article?.catalog]);

  if (!article) return null;

  const docId = article.document_id;
  const title = article.title || article.url || "Untitled";
  const type = article.target_type || article.type;
  const target = article.target || null;
  const aptName = article.apt_name || null;
  const source =
    article.source ||
    (() => {
      try {
        return new URL(article.url).hostname.replace("www.", "");
      } catch {
        return "";
      }
    })();
  const content = article.content || "";
  const timestamp = article.timestamp || "";
  const url = article.url || "";

  const handleLike = () => {
    const next = !localLike;
    setLocalLike(next);
    toggleThumb({ document_id: docId, action: "like" });
    let newDislike = localDislike;
    if (localDislike) {
      setLocalDislike(false);
      toggleThumb({ document_id: docId, action: "dislike" });
      newDislike = false;
    }
    onInteract?.(cardId, docId, {
      like: next,
      dislike: newDislike,
      catalog: localCatalog,
    });
  };

  const handleDislike = () => {
    const next = !localDislike;
    setLocalDislike(next);
    toggleThumb({ document_id: docId, action: "dislike" });
    let newLike = localLike;
    if (localLike) {
      setLocalLike(false);
      toggleThumb({ document_id: docId, action: "like" });
      newLike = false;
    }
    onInteract?.(cardId, docId, {
      like: newLike,
      dislike: next,
      catalog: localCatalog,
    });
  };

  const handleCatalog = () => {
    const next = !localCatalog;
    setLocalCatalog(next);
    toggleThumb({ document_id: docId, action: "catalog" });
    onInteract?.(cardId, docId, {
      like: localLike,
      dislike: localDislike,
      catalog: next,
    });
  };

  const switchMode = (mode) => {
    setViewMode(mode);
    setImgLoaded(false);
    setImgError(false);
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const modeBtn = (mode, ModeIcon, label) => {
    const isActive = viewMode === mode;
    return (
      <button
        onClick={() => switchMode(mode)}
        className={[
          "flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-normal flex-1",
          "transition relative z-10 cursor-pointer outline-none",
          isActive ? "text-mainText" : "text-mutedText hover:text-mainText",
        ].join(" ")}
      >
        <ModeIcon size={13} className="shrink-0" />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full bg-surface/80 backdrop-blur-md text-mainText font-poppins font-normal">
      {/* Header */}
      <div className="flex flex-col border-b border-mainDivider px-4 pt-4 pb-3 gap-3">
        {/* Top row: source + close */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[0.8125rem] font-normal text-mutedText truncate">
            {source || "Source"}
          </p>
          <button
            type="button"
            aria-label="Close sidebar"
            onClick={() => (onClose ? onClose() : setIsRightOpen(false))}
            className="shrink-0 p-1.5 rounded-lg text-mutedText hover:text-mainText hover:bg-mainButtonBg/50
                       transition cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2"
          >
            <X size={14} className="shrink-0" />
          </button>
        </div>

        {/* Bottom row: mode toggle + copy URL */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg p-0.5 flex-1 relative bg-innerBg">
            <motion.div
              className="absolute rounded-lg bg-mainButtonBg border border-inputBorder"
              style={{ top: 2, bottom: 2, width: "calc(50% - 4px)" }}
              animate={{ left: viewMode === "info" ? 2 : "calc(50% + 2px)" }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
            {modeBtn("info", FileText, "Details")}
            {modeBtn("screenshot", ImageIcon, "Screenshot")}
          </div>

          {url && (
            <button
              aria-label="Copy URL"
              className={[
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-normal transition",
                "cursor-pointer outline-none ring-inputFocus/60 focus-visible:ring-2",
                copied
                  ? "bg-velBlue/10 border border-velBlue/40 text-velBlue"
                  : "bg-innerBg border border-inputBorder text-mutedText hover:text-mainText hover:border-surfaceBorder",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              <AnimatePresence mode="wait">
                {copied ? (
                  <motion.div
                    key="check"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 800, damping: 15 }}
                    style={{ display: "flex" }}
                  >
                    <Check size={13} className="shrink-0" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="copy"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 800, damping: 15 }}
                    style={{ display: "flex" }}
                  >
                    <Copy size={13} className="shrink-0" />
                  </motion.div>
                )}
              </AnimatePresence>
              <span>{copied ? "Copied" : "Copy URL"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Content area */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{ scrollbarWidth: "thin" }}
      >
        {viewMode === "info" ? (
          <div className="p-4 flex flex-col gap-4" style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2 flex-wrap">
              {type && <TypeBadge type={type} />}
              {target && <TargetBadge target={target} />}
              {aptName && <AptBadge aptName={aptName} />}
            </div>

            <h2
              className="text-sm font-medium text-mainText leading-snug"
              style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {title}
            </h2>

            <div className="flex items-center gap-3 flex-wrap text-xs font-normal text-mutedText">
              {source && (
                <span
                  className="flex items-center gap-1"
                  style={{ wordBreak: "break-all" }}
                >
                  <Globe size={11} className="opacity-50 shrink-0" />
                  {source}
                </span>
              )}
              {timestamp && (
                <span className="flex items-center gap-1">
                  <Clock size={11} className="opacity-50 shrink-0" />
                  {timestamp}
                </span>
              )}
            </div>

            <div className="h-px w-full bg-mainDivider opacity-40" />

            {content ? (
              <div
                className="text-xs font-normal leading-relaxed whitespace-pre-wrap text-mutedText"
                style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
              >
                {content}
              </div>
            ) : (
              <p className="text-xs font-normal text-mutedText">
                No content available
              </p>
            )}
          </div>
        ) : (
          <div className="p-4 flex flex-col items-center gap-3">
            {article.screenshot_full || article.screenshot ? (
              <>
                {!imgLoaded && !imgError && (
                  <div className="flex items-center justify-center py-20">
                    <LoadingSpinner />
                  </div>
                )}
                {imgError && (
                  <p className="text-xs font-normal py-10 text-mutedText">
                    Screenshot failed to load
                  </p>
                )}
                <img
                  src={article.screenshot_full || article.screenshot}
                  alt="Full screenshot"
                  className="w-full rounded-lg border border-surfaceBorder"
                  style={{ display: imgLoaded ? "block" : "none" }}
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setImgError(true)}
                  draggable={false}
                />
              </>
            ) : (
              <p className="text-xs font-normal py-10 text-mutedText">
                No screenshot available
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer: interaction buttons */}
      <div className="flex items-center justify-center gap-4 px-4 py-3 border-t border-mainDivider">
        <SidebarActionBtn active={localLike} label="Like" onClick={handleLike}>
          <ThumbsUp
            className="h-5 w-5"
            strokeWidth={1.5}
            style={iconStyle(localLike)}
          />
        </SidebarActionBtn>
        <SidebarActionBtn
          active={localDislike}
          label="Dislike"
          onClick={handleDislike}
        >
          <ThumbsDown
            className="h-5 w-5"
            strokeWidth={1.5}
            style={iconStyle(localDislike)}
          />
        </SidebarActionBtn>
        <SidebarActionBtn
          active={localCatalog}
          label="Bookmark"
          onClick={handleCatalog}
        >
          <Bookmark
            className="h-5 w-5"
            strokeWidth={1.5}
            style={iconStyle(localCatalog)}
          />
        </SidebarActionBtn>
        <ShareArticleButton
          documentId={docId}
          article={article}
          variant="pill"
          iconSize={20}
        />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   useArticleSidebar — shared hook for opening ArticleDetailPanel
   ═══════════════════════════════════════════ */
export const useArticleSidebar = (articleCards, handleInteract) => {
  const dark = useDark();
  const t = getTheme(dark);

  const {
    isRightOpen,
    setRightComponent,
    setIsRightOpen,
    setRightSidebarMode,
    rightSidebarMode,
  } = useRightSidebar();

  const [activeCardId, setActiveCardId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);

  const handleCardClick = useCallback(
    (cardId, article) => {
      setActiveCardId(cardId);
      setSelectedCardId(cardId);
      setRightSidebarMode(null);
      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          article={article}
          cardId={cardId}
          t={getTheme(document.documentElement.classList.contains("dark"))}
          onInteract={handleInteract}
        />,
      );
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode, handleInteract],
  );

  useEffect(() => {
    if (!selectedCardId || !isRightOpen) return;
    if (
      rightSidebarMode === "team-reply" ||
      rightSidebarMode === "code-editor" ||
      rightSidebarMode === "public-citation"
    )
      return;
    const entry = articleCards.find((e) => e.id === selectedCardId);
    if (!entry) return;
    setRightComponent(
      <ArticleDetailPanel
        article={entry.article}
        cardId={selectedCardId}
        t={t}
        onInteract={handleInteract}
      />,
    );
  }, [articleCards, selectedCardId, isRightOpen, t, rightSidebarMode]);

  useEffect(() => {
    if (!isRightOpen) setActiveCardId(null);
  }, [isRightOpen]);

  return {
    activeCardId,
    setActiveCardId,
    selectedCardId,
    setSelectedCardId,
    handleCardClick,
  };
};

export default ArticleDetailPanel;
